/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ComponentProps, Ref } from 'preact';
import { forwardRef } from 'preact/compat';

import type { FocusableHandle } from '#hooks/UNSAFE_useFocusableTextField';
import type { TestIdProps } from '#hooks/UNSAFE_useTestId';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { FormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useFormFieldContextProps } from '#hooks/UNSAFE_useFormFieldContextProps';
import { RichSelectionField } from './RichSelectionField';
import { RichSelectionContext, type RichSelectionContextValue } from './RichSelectionContext';
import { useMessageSeverity } from '#hooks/PRIVATE_useMessageSeverity';
import { Size } from '#utils/UNSAFE_size';

type RichSelectionFieldProps = ComponentProps<typeof RichSelectionField>;

type Props = TestIdProps &
  Pick<
    RichSelectionFieldProps,
    | 'aria-describedby'
    | 'assistiveText'
    | 'baseId'
    | 'children'
    | 'columnSpan'
    | 'helpSourceLink'
    | 'helpSourceText'
    | 'isDisabled'
    | 'isReadonly'
    | 'isRequired'
    | 'label'
    | 'messages'
    | 'userAssistanceDensity'
  > & {
    /**
     * Specifies where the label is positioned relative to the field.
     */
    labelEdge?: 'none' | 'top' | 'inside' | 'start';
    /**
     * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
     */
    labelStartWidth?: Size;
    /**
     * Specifies the layout of the cards and media
     */
    layout: RichSelectionContextValue['layout'];
  } & (
    | {
        selection: 'single';
        value?: string | number;
      }
    | {
        selection: 'multiple';
        value?: Set<string | number>;
      }
  );

/**
 * Internal component used by Rich Selection components to provide context and wrap RichSelectionField
 */
export const BaseRichSelection = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      baseId,
      children,
      columnSpan,
      helpSourceLink,
      helpSourceText,
      isDisabled: propIsDisabled,
      isReadonly: propIsReadonly,
      isRequired,
      label,
      labelEdge: propLabelEdge,
      labelStartWidth: propLabelStartWidth,
      layout,
      messages,
      selection,
      testId,
      userAssistanceDensity: propUserAssistanceDensity,
      value
    }: Props,
    ref: Ref<FocusableHandle> = null
  ) => {
    const hasValue = value != null && (selection === 'single' || value.size > 0);

    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      labelEdge: formLabelEdge,
      labelStartWidth: formLabelStartWidth,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();

    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const labelEdge = propLabelEdge ?? formLabelEdge;
    const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;

    const formFieldContext = useFormFieldContextProps({
      hasValue,
      isDisabled,
      isReadonly
    });

    // rich selection only need to be aware of error severity
    const messageSeverity = useMessageSeverity(messages) === 'error' ? 'error' : undefined;

    return (
      <FormFieldContext.Provider value={formFieldContext}>
        <RichSelectionContext.Provider
          value={{
            selection,
            layout,
            messageSeverity
          }}>
          <RichSelectionField
            aria-describedby={ariaDescribedBy}
            assistiveText={assistiveText}
            baseId={baseId}
            columnSpan={columnSpan}
            hasValue={hasValue}
            helpSourceLink={helpSourceLink}
            helpSourceText={helpSourceText}
            isDisabled={isDisabled}
            isReadonly={isReadonly}
            isRequired={isRequired}
            label={label}
            labelEdge={labelEdge}
            labelStartWidth={labelStartWidth}
            messages={messages}
            parentComponentVariant={selection === 'single' ? 'richRadioSet' : 'richCheckboxSet'}
            ref={ref}
            role={selection === 'single' ? 'radiogroup' : 'group'}
            testId={testId}
            userAssistanceDensity={userAssistanceDensity}>
            {children}
          </RichSelectionField>
        </RichSelectionContext.Provider>
      </FormFieldContext.Provider>
    );
  }
);
